/**
 * When a social post was published, read off its link: Instagram shortcodes,
 * X status ids and TikTok video ids all carry their creation time. Used to
 * place a bulk import in the feed, since an import has no save dates of its
 * own (every row shares the moment it was inserted).
 */

const IG_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
/** Instagram media ids: milliseconds since this instant, shifted left 23 bits. */
const IG_EPOCH = 1314220021721;
/** X snowflakes: milliseconds since this instant, shifted left 22 bits. */
const X_EPOCH = 1288834974657;
/** Anything earlier is not a real post date (X snowflakes began late 2010). */
const EARLIEST = Date.UTC(2010, 10, 4);

const IG_RE = /instagram\.com\/(?:[^/?#]+\/)?(?:p|reels?|tv)\/([A-Za-z0-9_-]+)/i;
const X_RE = /(?:twitter|x)\.com\/[^/?#]+\/status(?:es)?\/(\d{15,20})/i;
const TIKTOK_RE = /tiktok\.com\/.*?\/video\/(\d{15,20})/i;

function instagram(code: string): number | null {
  // Private posts append extra characters; the media id is the first 11
  const id = code.slice(0, 11);
  let n = 0n;
  for (const ch of id) {
    const v = IG_ALPHABET.indexOf(ch);
    if (v < 0) return null;
    n = n * 64n + BigInt(v);
  }
  return Number(n >> 23n) + IG_EPOCH;
}

/** Milliseconds since the epoch, or null when the link carries no date. */
export function postedAtFromUrl(url: string | null | undefined, now = Date.now()): number | null {
  if (!url) return null;
  let at: number | null = null;
  try {
    const ig = IG_RE.exec(url);
    const x = ig ? null : X_RE.exec(url);
    const tt = ig || x ? null : TIKTOK_RE.exec(url);
    if (ig) at = instagram(ig[1]);
    else if (x) at = Number(BigInt(x[1]) >> 22n) + X_EPOCH;
    else if (tt) at = Number(BigInt(tt[1]) >> 32n) * 1000;
  } catch {
    return null;
  }
  // A date in the future or before these schemes existed means the id was not one
  if (at === null || !Number.isFinite(at) || at < EARLIEST || at > now + 86_400_000) return null;
  return at;
}
